import React, { useEffect, useState } from "react";

import { useHydrated } from "@app/components/client-only";
import { isWalmartDiscountCode } from "@app/helpers/discount-code";
import { useAppContext } from "@app/helpers/hooks";
import { discountCodeError } from "@app/tracking/checkout-tracking";
import { Transaction } from "@app/tracking/error-tracking";
import type { CurrencyCode } from "@app/types/currency";

import { CreditsRedeemedLayout, CreditsToRedeemLayout } from "./credits-layout";

export interface DiscountCodeProps {
  className?: string;
  initial_discount_code: string | null;
  // TODO-CART-V2: fix the type. Incompatibility issues between the legacy and new Thunks
  onSubmit: any;
  onReset: any;
  discount_amount: number | null | undefined;
  discount_currency: CurrencyCode;
  customLabel?: string;
  is_loading?: boolean;
}

const STATUS = {
  NO_CODE_SUBMITTED: "NO_CODE_SUBMITTED",
  VALID_CODE_APPLIED: "VALID_CODE_APPLIED",
  INVALID_CODE_SUBMITTED: "INVALID_CODE_SUBMITTED"
};

export const DISCOUNT_CODE_FIELD_ID = "discount_code";

const DiscountCode: React.FC<DiscountCodeProps> = ({
  className,
  initial_discount_code,
  onSubmit,
  onReset,
  discount_amount,
  discount_currency,
  customLabel,
  is_loading
}) => {
  const { translator, tracker } = useAppContext();
  const [status, setStatus] = useState(STATUS.NO_CODE_SUBMITTED);
  const [discount_code, setDiscountCode] = useState("");
  const [in_progress, setInProgress] = useState(false);
  const is_hydrated = useHydrated();
  const is_walmart_discount_code = isWalmartDiscountCode(discount_code);

  useEffect(() => {
    setDiscountCode(initial_discount_code || "");
    setStatus(
      initial_discount_code
        ? STATUS.VALID_CODE_APPLIED
        : STATUS.NO_CODE_SUBMITTED
    );
  }, [initial_discount_code]);

  function _renderForm() {
    return (
      <CreditsToRedeemLayout
        className={className}
        button_label={translator.t("!checkout.discount.validate")}
        onRedeem={submitDiscountCode}
        loading={in_progress}
        has_error={status === STATUS.INVALID_CODE_SUBMITTED}
        error_text={translator.t("!error.DiscountCodeInvalid.title")}
        inputProps={{
          labelText: translator.t("!checkout.summary.fees-taxes.code_discount"),
          onChange: handleChange,
          value: discount_code,
          id: DISCOUNT_CODE_FIELD_ID,
          // @ts-expect-error
          "data-testid": DISCOUNT_CODE_FIELD_ID
        }}
      />
    );
  }

  function _renderCode() {
    if (!is_loading && typeof discount_amount !== "number") {
      return null;
    }

    const label =
      customLabel ||
      `${translator.t(
        "!checkout.summary.fees-taxes.code_discount"
      )} ${discount_code}`;

    return (
      <CreditsRedeemedLayout
        balance={Number(discount_amount)}
        currency={discount_currency}
        onUnredeem={removeDiscountcode}
        isWalmartDiscountCode={is_walmart_discount_code}
        label={label}
      />
    );
  }

  function handleChange(event: {
    target: { value: React.SetStateAction<string> };
  }) {
    setDiscountCode(event.target.value);
    setStatus(STATUS.NO_CODE_SUBMITTED);
  }

  function submitDiscountCode(event: React.SyntheticEvent) {
    event.preventDefault();
    if (!discount_code) {
      return;
    }
    setInProgress(true);

    const discount_code_sanitized = discount_code.trim();

    onSubmit(discount_code_sanitized)
      .then(() => {
        setStatus(STATUS.VALID_CODE_APPLIED);
      })
      .catch((error: Error & { transaction?: string }) => {
        error.transaction = Transaction.ADD_DISCOUNT_CODE;
        tracker.track(discountCodeError(error));
        setStatus(STATUS.INVALID_CODE_SUBMITTED);
      })
      .finally(() => {
        setInProgress(false);
      });
  }

  function removeDiscountcode() {
    setInProgress(true);

    onReset(discount_code)
      .then(() => {
        setStatus(STATUS.NO_CODE_SUBMITTED);
        setDiscountCode("");
      })
      .catch((error: Error & { transaction?: string }) => {
        error.transaction = Transaction.REMOVE_DISCOUNT_CODE;
        tracker.track(discountCodeError(error));
      })
      .finally(() => {
        setInProgress(false);
      });
  }

  // Workaround for SSR hydration error on mobile
  if (!is_hydrated) {
    return null;
  }
  switch (status) {
    case STATUS.NO_CODE_SUBMITTED:
    case STATUS.INVALID_CODE_SUBMITTED:
      return _renderForm();

    case STATUS.VALID_CODE_APPLIED:
      return _renderCode();

    default:
      return null;
  }
};

export { DiscountCode };
